"use client";

import { useState } from "react";
import { ScrollArea } from "@/core/components/ui/scroll-area";
import { Button } from "@/core/components/ui/button";
import { ConfirmDialog } from "@/core/components/ui/confirm-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/core/components/ui/dropdown-menu";
import { EllipsisVertical, Pin, SquarePen, Trash2 } from "lucide-react";
import type { AgentType, Conversation } from "../types";
import { useTranslation } from "react-i18next";
import { AGENT_THEMES } from "../constants/agents";
import { resolveDisplayTextForAction } from "../utils/chatbotUtils";
import { IconlyCloseRemove, IconlyMenuOpenRight } from "@/core/assets/icons";
import { IconlyPenSquareBreak } from "@/core/assets/icons/iconly-pen-square-break";

/** Ordered list of sections shown in the history panel. `null` = no agent type. */
const AGENT_SECTION_ORDER: (AgentType | null)[] = [
  "operation",
  "estrategy",
  "experience",
  null,
];

type ConversationGroup = {
  type: AgentType | null;
  conversations: Conversation[];
};

function groupConversationsByAgent(
  conversations: Conversation[],
): ConversationGroup[] {
  const map = new Map<AgentType | null, Conversation[]>();
  for (const c of conversations) {
    const key = c.agent_type ?? null;
    const existing = map.get(key) ?? [];
    map.set(key, [...existing, c]);
  }
  return AGENT_SECTION_ORDER.filter((type) => map.has(type)).map((type) => ({
    type,
    conversations: map.get(type)!,
  }));
}

type ConversationHistoryPanelProps = {
  conversations: Conversation[];
  activeConversationId?: string | null;
  onNewConversation: () => void;
  onSelectConversation: (conversationId: string) => void;
  onRemoveConversation: (conversationId: string) => void | Promise<void>;
  /** Quando presente, exibe o botão X para fechar o painel (ex: Centro de ações). */
  onClose?: () => void;
};

// 30 caracteres: acima disso o título empurrava o menu de ações (3 pontinhos)
// para fora do card no painel do Centro de ações.
function truncateConversationTitle(title: string, maxLength = 30) {
  const normalizedTitle = title.trim();
  if (normalizedTitle.length <= maxLength) return normalizedTitle;
  return `${normalizedTitle.slice(0, maxLength)}...`;
}

export function ConversationHistoryPanel({
  conversations,
  activeConversationId,
  onNewConversation,
  onSelectConversation,
  onRemoveConversation,
  onClose,
}: ConversationHistoryPanelProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [conversationIdToRemove, setConversationIdToRemove] = useState<
    string | null
  >(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [translate] = useTranslation(["core"]);

  const groups = groupConversationsByAgent(conversations);

  return (
    <div className="h-full flex flex-col">
      {/* Header do painel de histórico */}
      <div className="flex shrink-0 items-center justify-between px-6 py-4">
        <div className="flex justify-between items-center gap-2 border-b border-border w-full pb-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onNewConversation}
            className="text-sm text-[#18181B] hover:text-warmgray-500 py-2 px-0 hover:bg-transparent flex items-center"
            aria-label={translate("chatbot-history-new-conversation") as string}
          >
            <IconlyPenSquareBreak size={18} color="currentColor" />{" "}
            <span className="ml-2">
              {translate("chatbot-history-new-conversation")}
            </span>
          </Button>
          <div className="flex items-center gap-1">
            {onClose && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="h-8 w-8 p-0 text-[#18181B] hover:text-foreground"
                aria-label={translate("chatbot-history-close") as string}
              >
                <IconlyMenuOpenRight size={18} color="currentColor" />
              </Button>
            )}
          </div>
        </div>
      </div>
      <ScrollArea className="flex-1">
        <div className="px-4 flex flex-col gap-4">
          {conversations.length === 0 ? (
            <div className="text-sm text-darkgray-600 px-2">
              {translate("chatbot-history-empty")}
            </div>
          ) : (
            groups.map((group) => (
              <div key={group.type ?? "other"}>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-2 mb-2 mt-3">
                  {group.type
                    ? `${translate("chatbot-hub-title-prefix")} ${translate(AGENT_THEMES[group.type].nameKey)}`
                    : (translate("chatbot-history-group-other") as string)}
                </p>
                <ul className="flex flex-col gap-1">
                  {group.conversations.map((c) => (
                    <li key={c.id}>
                      <div
                        className={`group flex items-center justify-between gap-2 ${c.id === activeConversationId ? "bg-neutral-200 rounded-md" : ""}`}
                      >
                        <button
                          type="button"
                          onClick={() => onSelectConversation(c.id)}
                          className={`flex-1 text-left p-2 min-w-0 rounded-md
                          }`}
                          aria-label={
                            translate("chatbot-history-open-conversation", {
                              title:
                                resolveDisplayTextForAction(
                                  c.title,
                                  (key) => translate(key) as string | undefined,
                                ) ?? c.title,
                            }) as string
                          }
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            {c.pinned && (
                              <Pin className="w-4 h-4 text-aiBlue-700" />
                            )}
                            <span className="truncate text-sm font-semibold text-darkgray-600">
                              {truncateConversationTitle(
                                resolveDisplayTextForAction(
                                  c.title,
                                  (key) => translate(key) as string | undefined,
                                ) ?? c.title,
                              )}
                            </span>
                          </div>
                        </button>

                        <div className="pr-2">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                disabled={isRemoving}
                                className="h-8 w-8 p-0 text-darkgray-500 hover:bg-transparent"
                                aria-label={
                                  translate(
                                    "chatbot-history-open-menu",
                                  ) as string
                                }
                                onClick={(e) => e.stopPropagation()}
                              >
                                <EllipsisVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              side="bottom"
                              align="end"
                              className="shadow-sm rounded-lg"
                            >
                              <DropdownMenuItem
                                className="gap-2 items-center text-sm text-danger-500 [&_svg]:text-danger-500 focus:bg-[#EBEBEB] focus:text-danger-500 data-[highlighted]:bg-[#EBEBEB] data-[highlighted]:text-danger-500"
                                disabled={isRemoving}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setConversationIdToRemove(c.id);
                                  setConfirmOpen(true);
                                }}
                              >
                                <Trash2 className="w-3 h-3 mt-[-3px]" />
                                {translate("chatbot-history-delete-item")}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </div>
      </ScrollArea>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          setConfirmOpen(open);
          if (!open) setConversationIdToRemove(null);
        }}
        title={translate("chatbot-history-remove-dialog-title") as string}
        description={
          translate("chatbot-history-remove-dialog-description") as string
        }
        confirmLabel={translate("chatbot-history-remove-confirm") as string}
        cancelLabel={translate("chatbot-history-remove-cancel") as string}
        confirmVariant="destructive"
        confirmDisabled={!conversationIdToRemove || isRemoving}
        onConfirm={async () => {
          if (!conversationIdToRemove) return;
          setIsRemoving(true);
          try {
            await onRemoveConversation(conversationIdToRemove);
          } finally {
            setIsRemoving(false);
          }
        }}
      />
    </div>
  );
}
